import { BlankSlate } from "@msuite/ui/blank-slate";
import { Virtuoso } from "@msuite/ui/libs/virtuoso";
import { guardString } from "@msuite/utils/strings";
import type { GroupedAbsenceListItem } from "@msuite/utils/types";
import { useCallback, useMemo } from "react";
import { useAbsences } from "@/hooks/use-absences";
import { useTeamPicker } from "@/hooks/use-team-picker";
import { AbsencesCalendarControls } from "./absences-calendar-controls";
import { AbsencesCalendarHeader } from "./absences-calendar-header";
import { AbsencesCalendarMemberRow } from "./absences-calendar-member-row";
import { AbsencesCalendarContext } from "./context";
import type { AbsencesCalendarController } from "./use-absences-calendar";

/** Props Interface */
interface AbsencesCalendarProps {
	controller: AbsencesCalendarController;
	searchValue?: string;
}

export function AbsencesCalendar({ controller, searchValue }: AbsencesCalendarProps) {
	/** Hooks */
	const teamPickerController = useTeamPicker();
	const { grouped } = useAbsences(
		{ teamId: guardString(teamPickerController.teamId) },
		{ enabled: !!teamPickerController.teamId },
	);

	/** Computed */
	const filteredGrouped = useMemo(() => {
		if (!searchValue) return grouped;
		const lower = searchValue.toLowerCase();
		return grouped.filter((g) => g.userName.toLowerCase().includes(lower));
	}, [grouped, searchValue]);

	const contextValue = useMemo(() => ({ controller }), [controller]);

	const itemContent = useCallback(
		(_: number, item: GroupedAbsenceListItem) => <AbsencesCalendarMemberRow item={item} />,
		[],
	);

	if (!teamPickerController.isReady || !teamPickerController.teamId)
		return <BlankSlate isLoading />;

	/** Render */
	return (
		<AbsencesCalendarContext.Provider value={contextValue}>
			<div className="v-stack grow-0 pb-4">
				<AbsencesCalendarControls teamPickerController={teamPickerController} />
				<div className="card v-stack gap-0 flex-1 p-0 min-w-0 overflow-hidden">
					<AbsencesCalendarHeader />
					<Virtuoso
						className="flex-1 scrollbar-hidden"
						data={filteredGrouped}
						itemContent={itemContent}
					/>
				</div>
			</div>
		</AbsencesCalendarContext.Provider>
	);
}
